import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import { tenant, type TenantSettings } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { applyBranding } from '@/shared/branding/applyBranding';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';

import {
  BrokerChannelTypesPanel,
  BrokerRequestDocumentsPanel,
} from '../brokers/BrokerSettingsPanels';

import { AccessSettingsPanel } from './AccessSettingsPanel';
import { alteracoes } from './alteracoes';
import { AuditTrailPanel } from './AuditTrailPanel';
import { DeclineReasonsPanel } from './DeclineReasonsPanel';
import { PaymentMethodsPanel } from './PaymentMethodsPanel';
import { PlatformLimitsCard } from './PlatformLimitsCard';
import { CardPrioritiesPanel, PipeStepSlaPanel } from './PipeSettingsPanels';
import { BusinessCalendarPanel } from './BusinessCalendarPanel';
import { StageSlaPanel } from './StageSlaPanel';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { TermsSettingsPanel } from '@/features/terms/TermsSettingsPanel';
import { veAnaliseDeCredito } from '@/features/creditSettings/acesso';
import { EntradaDaAnaliseDeCredito } from '@/features/creditSettings/EntradaDaAnaliseDeCredito';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Configuracoes da empresa.
 *
 * A identidade visual e aplicada em tempo de execucao, sem build por cliente. As
 * cores entram como variaveis no elemento raiz, e a previa abaixo usa exatamente o
 * mesmo caminho da aplicacao inteira.
 *
 * Nenhum segredo aparece aqui. Credencial de integracao vive no servidor.
 */
type Aba =
  | 'empresa'
  | 'acesso'
  | 'sla'
  | 'feriados'
  | 'motivos'
  | 'prioridades'
  | 'meios'
  | 'termos'
  | 'auditoria'
  | 'canais_corretor'
  | 'documentos_corretor'
  | 'credito';

const abas: { chave: Aba; rotulo: string }[] = [
  { chave: 'empresa', rotulo: 'Empresa' },
  { chave: 'acesso', rotulo: 'Acesso' },
  { chave: 'sla', rotulo: 'Prazo por etapa' },
  // P-BI-1: horario comercial e feriados, a base do prazo em dias uteis.
  { chave: 'feriados', rotulo: 'Feriados' },
  { chave: 'motivos', rotulo: 'Motivos padronizados' },
  // Esteiras (P-EST-1): as prioridades do cartao.
  { chave: 'prioridades', rotulo: 'Prioridades' },
  // P-TOM-C1: a lista que a conta bancaria do tomador oferece.
  { chave: 'meios', rotulo: 'Meios de pagamento' },
  // P-USU-2: o termo de uso, o termo de aceite da proposta e o aceite obrigatorio.
  { chave: 'termos', rotulo: 'Termos' },
  { chave: 'auditoria', rotulo: 'Auditoria' },
  // Ficha do corretor (P-COR-1): os paineis ficam no modulo de corretores.
  { chave: 'canais_corretor', rotulo: 'Canais do corretor' },
  { chave: 'documentos_corretor', rotulo: 'Documentos pedidos ao corretor' },
  // Configuracao da analise de credito (C-ADM): paginas proprias em /configuracoes/credito,
  // e a secao de pareceres, alcadas e bloqueios (C-ALC).
  { chave: 'credito', rotulo: 'Analise de credito' },
];

export function SettingsPage() {
  const { can, hasFeature, session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeEditar = can('configuracoes.tenant.update');
  // P-USU-2: a aba Termos so aparece para quem le as versoes do termo.
  const abasVisiveis = abas.filter(
    (item) =>
      (item.chave !== 'termos' || can('termos.termo.view')) &&
      (item.chave !== 'credito' || veAnaliseDeCredito(can, hasFeature)),
  );

  const consulta = useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: ({ signal }) => tenant.current(signal),
  });

  const [aba, setAba] = useState<Aba>('empresa');
  const [form, setForm] = useState<Partial<TenantSettings>>({});
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);

  // Ajuste de estado durante a renderizacao, e nao em efeito. Quando os dados
  // chegam, o formulario nasce ja preenchido: um efeito faria a tela renderizar
  // uma vez vazia antes de preencher, o que aparece como piscada.
  if (consulta.data && carregadoDe !== consulta.data.id) {
    setCarregadoDe(consulta.data.id);
    setForm(consulta.data.settings);
  }

  const salvar = useMutation({
    mutationFn: (input: Partial<TenantSettings>) => tenant.updateSettings(input),
    onSuccess: (settings) => {
      toast.success('Identidade visual atualizada.');
      // A aplicacao inteira reflete a mudanca sem recarregar: as cores sao
      // variaveis no elemento raiz.
      applyBranding(settings);
      void queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
  });

  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  function set(field: keyof TenantSettings, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    // So o que mudou. Mandar a configuracao inteira reenviava o dominio proprio, que
    // esta tela nem mostra, e exigia a funcionalidade de dominio de quem so trocou
    // uma cor.
    salvar.mutate(alteracoes(consulta.data?.settings ?? {}, form));
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Configuracoes"
        description="Dados da empresa, identidade visual, prazos e catalogos."
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as configuracoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      {/* As abas separam assuntos que mudam por motivos diferentes: a identidade
          visual e do marketing, o prazo e do acordo de servico, o catalogo e da
          politica de subscricao e a trilha e de auditoria. */}
      <div
        role="tablist"
        aria-label="Configuracoes"
        className="flex gap-1 border-b border-line"
      >
        {abasVisiveis.map((item) => (
          <button
            key={item.chave}
            type="button"
            role="tab"
            id={`aba-${item.chave}`}
            aria-selected={aba === item.chave}
            aria-controls={`painel-${item.chave}`}
            onClick={() => setAba(item.chave)}
            className={
              aba === item.chave
                ? 'border-b-2 border-brand-500 px-3 py-2 text-sm font-semibold text-ink-900'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-ink-500 hover:text-ink-900'
            }
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      {aba === 'acesso' && consulta.data ? (
        <AccessSettingsPanel
          key={consulta.data.id}
          settings={consulta.data.settings}
          slug={consulta.data.slug}
          podeEditar={podeEditar}
        />
      ) : null}
      {aba === 'sla' ? (
        <div className="flex flex-col gap-5">
          <StageSlaPanel />
          <PipeStepSlaPanel />
        </div>
      ) : null}
      {aba === 'feriados' ? <BusinessCalendarPanel /> : null}
      {aba === 'motivos' ? <DeclineReasonsPanel /> : null}
      {aba === 'prioridades' ? <CardPrioritiesPanel /> : null}
      {aba === 'meios' ? <PaymentMethodsPanel /> : null}
      {aba === 'termos' ? <TermsSettingsPanel /> : null}
      {aba === 'auditoria' ? <AuditTrailPanel /> : null}
      {aba === 'canais_corretor' ? <BrokerChannelTypesPanel /> : null}
      {aba === 'documentos_corretor' ? <BrokerRequestDocumentsPanel /> : null}
      {aba === 'credito' ? <EntradaDaAnaliseDeCredito /> : null}

      <div className={aba === 'empresa' ? 'grid gap-5 lg:grid-cols-3' : 'hidden'}>
        <Card className="lg:col-span-2">
          <CardHeader
            title="Identidade visual"
            description="Aplicada em tempo de execucao, sem gerar uma versao por empresa."
          />
          <CardBody>
            {consulta.isPending ? (
              <SkeletonText lines={6} />
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-5">
                {salvar.isError ? (
                  <Alert tone="danger" title="Nao foi possivel salvar">
                    {salvar.error.message}
                  </Alert>
                ) : null}

                <fieldset
                  disabled={!podeEditar || salvar.isPending}
                  className="flex flex-col gap-5"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Nome de exibicao"
                      value={form.display_name ?? ''}
                      error={fieldErrorOf(apiError, 'display_name')}
                      onChange={(event) => set('display_name', event.target.value)}
                    />
                    <Field
                      label="Nome do aplicativo instalavel"
                      value={form.pwa_name ?? ''}
                      onChange={(event) => set('pwa_name', event.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <CampoDeCor
                      label="Cor primaria"
                      value={form.primary_color ?? ''}
                      error={fieldErrorOf(apiError, 'primary_color')}
                      onChange={(value) => set('primary_color', value)}
                    />
                    <CampoDeCor
                      label="Cor de destaque"
                      value={form.accent_color ?? ''}
                      error={fieldErrorOf(apiError, 'accent_color')}
                      onChange={(value) => set('accent_color', value)}
                    />
                    <CampoDeCor
                      label="Cor do tema"
                      value={form.theme_color ?? ''}
                      error={fieldErrorOf(apiError, 'theme_color')}
                      onChange={(value) => set('theme_color', value)}
                    />
                    <CampoDeCor
                      label="Cor de fundo"
                      value={form.background_color ?? ''}
                      error={fieldErrorOf(apiError, 'background_color')}
                      onChange={(value) => set('background_color', value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="URL do logotipo"
                      value={form.logo_url ?? ''}
                      onChange={(event) => set('logo_url', event.target.value)}
                    />
                    <Field
                      label="URL do simbolo"
                      value={form.logo_symbol_url ?? ''}
                      onChange={(event) => set('logo_symbol_url', event.target.value)}
                    />
                  </div>
                </fieldset>

                {podeEditar ? (
                  <div className="flex justify-end border-t border-line pt-4">
                    <Button type="submit" loading={salvar.isPending}>
                      Salvar identidade visual
                    </Button>
                  </div>
                ) : (
                  <Alert tone="info" title="Somente leitura">
                    O seu perfil nao inclui a alteracao das configuracoes da empresa.
                  </Alert>
                )}
              </form>
            )}
          </CardBody>
        </Card>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader title="Empresa" />
            <CardBody className="flex flex-col gap-3 text-sm">
              <Linha
                rotulo="Nome"
                valor={consulta.data?.name ?? session?.tenant.name ?? ''}
              />
              <Linha rotulo="Identificador" valor={consulta.data?.slug ?? ''} />
              <Linha rotulo="Plano" valor={consulta.data?.plan ?? ''} />
              <Linha rotulo="Situacao" valor={consulta.data?.status ?? ''} />
            </CardBody>
          </Card>

          {/* P-PLAT: o que a administracao da plataforma decidiu para a empresa. */}
          <PlatformLimitsCard />

          <Card>
            <CardHeader
              title="Integracoes"
              description="Nenhuma integracao configurada."
            />
            <CardBody className="flex flex-col gap-3">
              {[
                'Receita Federal',
                'SUSEP',
                'Assinatura eletronica',
                'Analise de credito',
              ].map((nome) => (
                <div
                  key={nome}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-ink-600">{nome}</span>
                  <Badge tone="neutral">Nao configurada</Badge>
                </div>
              ))}
              <p className="mt-1 text-2xs text-ink-500">
                Uma integracao so aparece como ativa quando estiver realmente
                configurada. Enquanto isso, nenhum dado externo e apresentado como se
                tivesse vindo do provedor.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-500">{rotulo}</span>
      <span className="truncate font-medium text-ink-900">{valor}</span>
    </div>
  );
}

function CampoDeCor({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Field
          label={label}
          value={value}
          error={error}
          placeholder="#1d6cf2"
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      <span
        aria-hidden
        className="mb-0.5 size-10 shrink-0 rounded-md border border-line-strong"
        style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(value) ? value : undefined }}
      />
    </div>
  );
}
